import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCopilotStore } from '@/app/lib/stores/useCopilotStore';
import { createCacheInvalidationService } from '@/app/lib/services/cacheInvalidationService';
import { websocketService } from '@/app/lib/services/websocketService';

/**
 * Hook to connect cache invalidation service to the copilot WebSocket
 * This should be used in a top-level component where React Query is available
 */
export function useCopilotCacheInvalidation() {
  const queryClient = useQueryClient();
  const cacheInvalidationServiceRef = useRef<ReturnType<typeof createCacheInvalidationService>>();
  const isConnectedRef = useRef<boolean>(false);

  useEffect(() => {
    // Create cache invalidation service if not already created
    if (!cacheInvalidationServiceRef.current) {
      cacheInvalidationServiceRef.current = createCacheInvalidationService(queryClient);
    }

    // Set up invalidation handler
    const handleInvalidation = async (entities: string[]) => {
      console.log('[useCopilotCacheInvalidation] Processing invalidation for entities:', entities);
      
      if (cacheInvalidationServiceRef.current) {
        await cacheInvalidationServiceRef.current.invalidateEntities(entities);
      }
    };

    // Get current copilot store state
    const state = useCopilotStore.getState();
    
    // Only set up the handler if we're connected
    if (state.isConnected && !isConnectedRef.current) {
      console.log('[useCopilotCacheInvalidation] Setting up invalidation handler');
      
      // Update WebSocket callbacks to include invalidation handler
      const currentCallbacks = (websocketService as any).callbacks || {};
      (websocketService as any).callbacks = {
        ...currentCallbacks,
        onInvalidationRequest: handleInvalidation,
      };
      
      isConnectedRef.current = true;
    } else if (!state.isConnected && isConnectedRef.current) {
      // Clean up when disconnected
      isConnectedRef.current = false;
    }

    // Subscribe to connection state changes
    const unsubscribe = useCopilotStore.subscribe((state) => {
      if (state.isConnected && !isConnectedRef.current) {
        console.log('[useCopilotCacheInvalidation] Connection established, setting up handler');
        
        // Update WebSocket callbacks to include invalidation handler
        const currentCallbacks = (websocketService as any).callbacks || {};
        (websocketService as any).callbacks = {
          ...currentCallbacks,
          onInvalidationRequest: handleInvalidation,
        };
        
        isConnectedRef.current = true;
      } else if (!state.isConnected && isConnectedRef.current) {
        isConnectedRef.current = false;
      }
    });

    return () => {
      unsubscribe();
    };
  }, [queryClient]);
}